/** Login Lit + FormComponent (hexagonal entrypoint). */
import { html, TemplateResult } from 'lit';
import { LitComponent } from '@src/modules/shell-ui/infrastructure/drivers/ui/components/LitComponent.ts';
import { FormComponent } from '@src/modules/shell-ui/infrastructure/drivers/ui/components/FormComponent.ts';
import type { FormFieldProps } from '@src/modules/shell-ui/infrastructure/drivers/ui/components/FormComponent.ts';
import type { ShellCorePorts } from '@src/core/bootstrap/ShellPorts.ts';
import { getErrorMessage } from '@core/domain/utils/errorHandler.ts';
import { BRAND_UI } from 'brand-ui-assets';
import { shellPwaInstallGuideTemplate } from '@src/modules/shell-ui/infrastructure/shellPwaInstallGuideUi.ts';
import {
  bindShellPwaInstallPrompt,
  getPwaInstallGuidanceKind,
  type PwaInstallGuidanceKind,
  triggerShellPwaInstall,
} from '@src/modules/shell-ui/infrastructure/shellPwaInstallUi.ts';

function joinMissingFieldLabelsEs(labels: string[]): string {
  const u = [...new Set(labels.map((s) => s.trim()).filter(Boolean))];
  if (u.length === 0) return '';
  const head = 'Para guardar, complete: ';
  if (u.length === 1) return `${head}${u[0]}.`;
  if (u.length === 2) return `${head}${u[0]} y ${u[1]}.`;
  const last = u.at(-1)!;
  return `${head}${u.slice(0, -1).join(', ')} y ${last}.`;
}

const LOGIN_FORM_SUBMIT_BUTTON_CLASS =
  'md-btn-filled w-full min-h-12 rounded-md font-medium shadow-[var(--md-elevation-1)] transition-opacity hover:opacity-95 active:opacity-90 disabled:cursor-not-allowed text-[color:var(--md-sys-color-on-primary)] bg-[color:var(--md-sys-color-primary)] py-3 px-6';

export function loginComponentTemplate(p: {
  appTitle: string;
  errorMessage: string;
  isLoading: boolean;
  keyboardOpen: boolean;
  showInstall: boolean;
  installGuide: PwaInstallGuidanceKind | null;
  onInstall: () => void;
}): TemplateResult {
  const logoSrc = BRAND_UI.appLoginLogo;
  const backgroundSrc = BRAND_UI.loginBackground;
  return html`
    <style>
      .login-root {
        position: relative;
        isolation: isolate;
        display: flex;
        min-height: 100dvh;
        align-items: center;
        justify-content: center;
        padding: max(1.5rem, env(safe-area-inset-top)) 1rem
          calc(max(1.5rem, env(safe-area-inset-bottom)) + var(--login-kb-offset, 0px));
        background-color: #000;
        background-image: url('${backgroundSrc}');
        background-size: cover;
        background-position: center;
        background-repeat: no-repeat;
        transition: padding-bottom var(--md-motion-duration-short2) var(--md-motion-easing-standard);
      }
      .login-root--keyboard {
        align-items: flex-start;
        padding-top: max(1rem, env(safe-area-inset-top));
      }
      .login-overlay {
        position: absolute;
        inset: 0;
        z-index: 0;
        pointer-events: none;
        background: color-mix(in srgb, var(--app-ink) 52%, transparent);
      }
      .login-card-wrap {
        position: relative;
        z-index: 1;
        width: 100%;
        max-width: 28rem;
      }
      .login-logo {
        display: block;
        width: min(100%, 15rem);
        height: auto;
        margin: 0 auto 1.25rem;
        object-fit: contain;
      }
    </style>
    <div class="login-root ${p.keyboardOpen ? 'login-root--keyboard' : ''}">
      <div class="login-overlay" aria-hidden="true"></div>
      <div class="login-card-wrap">
        <div
          class="login-card rounded-[var(--md-shape-corner-extra-large)] p-8 shadow-[var(--md-elevation-3)]"
          style="background: var(--md-sys-color-surface-container-high); color: var(--md-sys-color-on-surface);"
        >
          <div class="mb-8 text-center">
            <img
              class="login-logo shell-brand-logo"
              src="${logoSrc}"
              width="499"
              height="484"
              alt="Alcaldía de Medellín — Distrito de Ciencia, Tecnología e Innovación"
            />
            <p class="login-subtitle text-sm" style="color: var(--md-sys-color-on-surface);">Inicia sesión para continuar</p>
          </div>
          ${p.errorMessage
            ? html`
                <div
                  class="md-error-banner mb-6 flex items-start gap-3 rounded-md px-4 py-3 text-sm"
                  style="background: var(--md-sys-color-error-container); color: var(--md-sys-color-on-error-container);"
                  role="alert"
                >
                  <svg class="mt-0.5 h-5 w-5 shrink-0" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path
                      d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"
                    />
                  </svg>
                  <p>${p.errorMessage}</p>
                </div>
              `
            : null}
          <form-component id="login-form"></form-component>
          ${p.installGuide ? html`<div class="mt-4">${shellPwaInstallGuideTemplate(p.installGuide)}</div>` : null}
          ${p.showInstall
            ? html`
                <button
                  type="button"
                  class="md-btn-outlined md-sys-ripple touch-manipulation mt-4 w-full min-h-12 rounded-md py-3 px-6 text-sm font-medium"
                  @click=${p.onInstall}
                >
                  Instalar app
                </button>
              `
            : null}
        </div>
      </div>
    </div>
  `;
}

/** Returns the base properties for the login form. */
function loginFormBaseProps(
  onSubmit: (formData: FormData) => void | Promise<void>,
  isLoading: boolean,
): {
  fields: FormFieldProps[];
  submitLabel: string;
  onSubmit: (formData: FormData) => void | Promise<void>;
  layout?: string;
  submitButtonClass?: string;
  submitBusy?: boolean;
} {
  return {
    fields: [
      {
        name: 'email',
        label: 'Correo electrónico o usuario',
        type: 'text',
        required: true,
        placeholder: 'Ingrese su correo o usuario',
        autocomplete: 'username',
        inputmode: 'email',
        enterkeyhint: 'next',
      },
      {
        name: 'password',
        label: 'Contraseña',
        type: 'password',
        required: true,
        placeholder: 'Ingrese su contraseña',
        autocomplete: 'current-password',
        enterkeyhint: 'go',
      },
    ],
    onSubmit,
    submitLabel: isLoading ? 'Entrando…' : 'Entrar',
    submitBusy: isLoading,
    submitButtonClass: LOGIN_FORM_SUBMIT_BUTTON_CLASS,
  };
}

export class LoginComponent extends LitComponent {
  /** Generic shell login (no feature-specific form wiring). */
  shellPorts!: Pick<ShellCorePorts, 'appDisplayTitle' | 'auth' | 'telemetry' | 'notifyLoginSuccess'>;

  private errorMessage: string = '';
  private keyboardOpen = false;
  private showInstall = false;
  private installGuide: PwaInstallGuidanceKind | null = null;
  private formWireAttempts = 0;
  private unbindPwaInstall: (() => void) | null = null;
  private static readonly maxFormWireAttempts = 60;

  /** Wire the nested form after the shadow tree (and form-component) exist. */
  connectedCallback(): void {
    super.connectedCallback();
    this.bindKeyboardViewport();
    this.unbindPwaInstall = bindShellPwaInstallPrompt(() => this.syncInstallUi());
    this.syncInstallUi();
    this.scheduleLoginFormWire();
  }

  disconnectedCallback(): void {
    this.unbindKeyboardViewport();
    this.unbindPwaInstall?.();
    this.unbindPwaInstall = null;
    super.disconnectedCallback();
  }

  private bindKeyboardViewport(): void {
    const vv = globalThis.visualViewport;
    if (!vv) return;
    vv.addEventListener('resize', this.onViewportChange);
    vv.addEventListener('scroll', this.onViewportChange);
    this.onViewportChange();
  }

  private unbindKeyboardViewport(): void {
    const vv = globalThis.visualViewport;
    if (!vv) return;
    vv.removeEventListener('resize', this.onViewportChange);
    vv.removeEventListener('scroll', this.onViewportChange);
  }

  private readonly onViewportChange = (): void => {
    const vv = globalThis.visualViewport;
    if (!vv) return;
    const kbInset = Math.max(0, globalThis.innerHeight - vv.height - vv.offsetTop);
    const keyboardOpen = kbInset > 48;
    if (this.shadow?.host instanceof HTMLElement) {
      this.shadow.host.style.setProperty('--login-kb-offset', `${Math.round(kbInset)}px`);
    }
    if (keyboardOpen === this.keyboardOpen) return;
    this.keyboardOpen = keyboardOpen;
    this.update();
  };

  /** Renders the component and re-syncs form props (submit label, handler). */
  protected render(): void {
    super.render();
    this.scheduleLoginFormWire();
  }

  protected update(): void {
    super.update();
    this.scheduleLoginFormWire();
  }

  private scheduleLoginFormWire(): void {
    this.formWireAttempts = 0;
    queueMicrotask(() => this.wireLoginForm());
  }

  /** Returns the login component template. */
  protected getTemplate(): TemplateResult {
    return loginComponentTemplate({
      appTitle: this.shellPorts.appDisplayTitle,
      errorMessage: this.errorMessage,
      isLoading: this.isLoading,
      keyboardOpen: this.keyboardOpen,
      showInstall: this.showInstall,
      installGuide: this.installGuide,
      onInstall: this.handleInstall,
    });
  }

  private syncInstallUi(): void {
    const kind = getPwaInstallGuidanceKind();
    this.showInstall = kind === 'chromium-prompt';
    this.installGuide = kind && kind !== 'chromium-prompt' ? kind : null;
    this.update();
  }

  private readonly handleInstall = async (): Promise<void> => {
    const outcome = await triggerShellPwaInstall();
    if (outcome === 'unavailable') return;
    this.syncInstallUi();
  };

  /** Binds submit handler and fields on nested form-component (retries until its shadow exists). */
  private wireLoginForm(): void {
    if (!this.isConnected || !this.shadow) return;
    const form = this.shadow.getElementById('login-form') as FormComponent | null;
    if (!form?.shadowRoot) {
      if (this.formWireAttempts < LoginComponent.maxFormWireAttempts) {
        this.formWireAttempts += 1;
        requestAnimationFrame(() => this.wireLoginForm());
      }
      return;
    }
    this.formWireAttempts = 0;
    form.setProps(loginFormBaseProps(this.handleSubmit, this.isLoading));
  }

  /** Handles the login form submission. */
  private readonly handleSubmit = async (formData: FormData): Promise<void> => {
    this.errorMessage = '';
    const usernameOrEmail = (formData.get('email') as string) || '';
    const password = (formData.get('password') as string) || '';
    const faltan: string[] = [];
    if (!usernameOrEmail.trim()) faltan.push('Correo electrónico o usuario');
    if (!password) faltan.push('Contraseña');
    if (faltan.length > 0) {
      this.errorMessage = joinMissingFieldLabelsEs(faltan);
      this.update();
      return;
    }

    await this.executeWithLoading(
      async () => {
        await this.shellPorts.auth.login(usernameOrEmail, password);
        if (!(await this.shellPorts.auth.isAuthenticated())) {
          throw new Error('No se pudo guardar la sesión en el dispositivo.');
        }
        this.shellPorts.telemetry.trackEvent('auth.login.success', {});

        await this.shellPorts.notifyLoginSuccess();
        return true;
      },
      () => {
        this.isLoading = true;
      },
      () => {
        this.isLoading = false;
      },
      (error: Error) => {
        this.isLoading = false;
        this.errorMessage = getErrorMessage(error) || 'No se pudo iniciar sesión. Revise usuario y contraseña.';
        this.shellPorts.telemetry.trackEvent('auth.login.failure', {
          message: this.errorMessage,
        });
      },
    );
  };
}

customElements.define('login-component', LoginComponent);
